import { Badge } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { StatusChip, type ChipTone } from '@/design';
import type { FactStatus } from '../api/participant-contracts';
import { PayloadTree } from './payload-tree';

/**
 * A registry's answer under one fact key, as every screen that shows one draws it — the person
 * hub's registry answers (SR-215, the participant card's before it) and the SEC ADV firm card's
 * prefill preview (survey-api's SR-214): the key, the status as the wire spells it, a sentence
 * saying what that status means, survey-api's reading of the answer where it made one, the
 * answer's dates, and **the answer as stored, whole and folded** (`PayloadTree`, roadmap D8).
 *
 * It lived in the participant card until the firm card became its second reader, and moved
 * unchanged. **One rendering per thing two screens show**: a registry's answer on
 * one screen and the same answer previewed on another must not come to be laid out, or folded,
 * two ways — so the preview folds its payload too, although it has no reading above it.
 *
 * **Folded, because it is no longer what staff read.** Until SR-215 the stored document was the
 * only drawing of an answer, open at every depth; FINRA's record is a JSON string inside its
 * envelope, and nobody could read it (the assessment's §1.4). survey-api now projects it
 * (`reading`, the person hub design §5), and the payload stays underneath as the evidence the
 * reading was made from — every member still drawn, one click away. The alternative, dropping
 * it, would leave a reading nobody could check against what the registry actually sent.
 *
 * What stays with each screen is what only it can say: the sentence — the hub speaks of what a
 * registry last answered and whether anything on it asks again, the preview of what a response
 * opened now would pin — the reading, and the dates, since a stored answer has a fetch time and
 * a preview, asked now, has none. So they come in from the caller, the dates and any control as
 * `children` laid out in this item's two-column grid.
 *
 * An `error`'s reason is inside its payload, and is read there: nothing here picks a member out
 * of an answer, for `PayloadTree`'s reason.
 */

const FACT_STATUS_VARIANT = {
  ok: 'success',
  not_found: 'soft',
  error: 'destructive'
} as const;

export function RegistryFact({
  fact,
  words,
  reading,
  payloadLabel = 'The answer as stored',
  foldLabel = 'As stored',
  tone,
  children
}: {
  fact: { factKey: string; status: FactStatus; payload: unknown };
  /** What the status means on this screen. */
  words: React.ReactNode;
  /** survey-api's reading of the answer, where it made one — drawn above the dates. */
  reading?: React.ReactNode;
  /** The payload's accessible name. */
  payloadLabel?: string;
  /** The fold line the payload sits under. */
  foldLabel?: string;
  /** A tone for the status in place of the registries' badge — for a reading of Veritant's, whose
   *  `ok` is not a success (SR-416's wirehouse list and U4 readings). */
  tone?: ChipTone;
  /** The answer's dates, as `Fact`s, and anything the screen offers on it. */
  children: React.ReactNode;
}) {
  return (
    <li className="grid gap-3 rounded-md border border-border p-4 text-sm sm:grid-cols-2">
      <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
        <code className="text-xs">{fact.factKey}</code>
        {/* Closed in the type, open on the wire: a status survey-api adds later still draws. */}
        {tone ? (
          <StatusChip tone={tone}>{fact.status}</StatusChip>
        ) : (
          <Badge variant={FACT_STATUS_VARIANT[fact.status] ?? 'outline'}>{fact.status}</Badge>
        )}
      </div>
      <p className="sm:col-span-2">{words}</p>
      {reading && <div className="min-w-0 sm:col-span-2">{reading}</div>}
      {children}
      {/* Closed as the initial state only: React leaves the element's own toggle alone, so an
          answer opened by hand stays open when the page re-reads itself. */}
      <details className="sm:col-span-2">
        <summary className="cursor-pointer text-muted-foreground select-none">{foldLabel}</summary>
        <div className="mt-2">
          <PayloadTree value={fact.payload} label={payloadLabel} />
        </div>
      </details>
    </li>
  );
}
